/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, DollarSign, CalendarDays, CalendarRange, TrendingUp, Hash, Sigma, Download } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useAdmin } from './AdminAuth';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dayKey = (ts) => new Date(ts * 1000).toISOString().slice(0, 10);
const fmtDate = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'custom', label: 'Custom Date' },
];

const RevenueCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-[#0f1712]/10 bg-white p-5">
    <div className="w-10 h-10 rounded-xl bg-[#0e9f6e]/12 flex items-center justify-center mb-3">
      <Icon size={18} className="text-[#0e9f6e]" />
    </div>
    <p className="font-display text-2xl font-700 text-[#0f1712] tracking-tight">{value}</p>
    <p className="text-xs text-[#0f1712]/50 mt-1">{label}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    succeeded: 'text-[#0e9f6e] bg-[#e8f5ee]',
    refunded: 'text-[#b7791f] bg-[#fff4e5]',
    failed: 'text-[#d64545] bg-[#fbe9e9]',
  };
  return <span className={`inline-block text-xs font-semibold rounded-full px-2.5 py-1 capitalize ${map[status] || 'text-[#0f1712]/50 bg-[#f6f6f2]'}`}>{status}</span>;
};

const RevenuePanel = () => {
  const { authAxios } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [range, setRange] = useState('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const res = await authAxios.get('/admin/revenue');
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load revenue from Stripe.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const bounds = useMemo(() => {
    const now = new Date();
    if (range === 'today') { const d = new Date(now); d.setHours(0, 0, 0, 0); return [d.getTime() / 1000, Infinity]; }
    if (range === '7d') { const d = new Date(now); d.setDate(d.getDate() - 6); d.setHours(0, 0, 0, 0); return [d.getTime() / 1000, Infinity]; }
    if (range === 'month') { const d = new Date(now.getFullYear(), now.getMonth(), 1); return [d.getTime() / 1000, Infinity]; }
    if (range === 'custom') {
      const from = customFrom ? new Date(customFrom + 'T00:00:00').getTime() / 1000 : 0;
      const to = customTo ? new Date(customTo + 'T23:59:59').getTime() / 1000 : Infinity;
      return [from, to];
    }
    return [0, Infinity];
  }, [range, customFrom, customTo]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    return all.filter((r) => r.ts >= bounds[0] && r.ts <= bounds[1]);
  }, [data, bounds]);

  const chart = useMemo(() => {
    const map = {};
    rows.forEach((r) => { const k = dayKey(r.ts); map[k] = (map[k] || 0) + Number(r.flix_revenue || 0); });
    return Object.keys(map).sort().map((k) => ({ date: k.slice(5), revenue: Math.round(map[k] * 100) / 100 }));
  }, [rows]);

  const exportCsv = () => {
    const head = ['Date', 'Merchant Name', 'Customer Name', 'Sale Amount', 'Stripe Fee', 'FLIX Revenue', 'Merchant Net', 'Status'];
    const lines = rows.map((r) => [
      new Date(r.date).toISOString(), r.merchant, r.customer,
      r.sale_amount, r.stripe_fee, r.flix_revenue, r.merchant_net, r.status,
    ].map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `flix-revenue-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const c = data?.cards || {};

  if (loading) return <div className="p-10 text-center text-[#0f1712]/40 text-sm">Loading revenue from Stripe…</div>;

  return (
    <div data-testid="flix-revenue-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-700 text-[#0f1712]">FLIX Revenue</h2>
          <p className="text-[#0f1712]/50 text-sm mt-1">Live platform earnings from Stripe Connect application fees.</p>
        </div>
        <button data-testid="revenue-refresh-btn" onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-[#0f1712]/15 px-4 py-2.5 text-sm font-semibold text-[#0f1712] hover:border-[#0e9f6e] transition-colors">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {err && <div className="rounded-xl bg-[#fbe9e9] border border-[#d64545]/20 text-[#d64545] text-sm px-4 py-3 mb-5">{err}</div>}

      {/* Revenue cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <RevenueCard testid="card-today" icon={DollarSign} label="Today's Revenue" value={money(c.today)} />
        <RevenueCard testid="card-week" icon={CalendarDays} label="This Week" value={money(c.week)} />
        <RevenueCard testid="card-month" icon={CalendarRange} label="This Month" value={money(c.month)} />
        <RevenueCard testid="card-all-time" icon={TrendingUp} label="All Time Revenue" value={money(c.all_time)} />
        <RevenueCard testid="card-count" icon={Hash} label="Total Transactions" value={Number(c.count || 0).toLocaleString()} />
        <RevenueCard testid="card-avg" icon={Sigma} label="Avg Revenue / Transaction" value={money(c.avg)} />
      </div>

      {/* Chart */}
      <div className="rounded-2xl border border-[#0f1712]/10 bg-white p-6 mb-6">
        <h3 className="font-display text-lg font-700 text-[#0f1712] mb-4">Daily FLIX Revenue</h3>
        {chart.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-[#0f1712]/40 text-sm">No revenue in this period.</div>
        ) : (
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={chart} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0e9f6e" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0e9f6e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#0f1712" strokeOpacity={0.06} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#0f171280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#0f171280' }} axisLine={false} tickLine={false} width={50} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: '1px solid #0f171215', fontSize: 13 }} />
                <Area type="monotone" dataKey="revenue" stroke="#0e9f6e" strokeWidth={2.5} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Filters + export */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((r) => (
            <button key={r.key} data-testid={`revenue-range-${r.key}`} onClick={() => setRange(r.key)}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${range === r.key ? 'bg-[#0f1712] text-white' : 'bg-white text-[#0f1712]/70 border border-[#0f1712]/10 hover:border-[#0e9f6e]'}`}>
              {r.label}
            </button>
          ))}
          {range === 'custom' && (
            <div className="flex items-center gap-2">
              <input data-testid="revenue-custom-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded-xl border border-[#0f1712]/15 px-3 py-2 text-sm outline-none focus:border-[#0e9f6e]" />
              <span className="text-[#0f1712]/40 text-sm">to</span>
              <input data-testid="revenue-custom-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                className="rounded-xl border border-[#0f1712]/15 px-3 py-2 text-sm outline-none focus:border-[#0e9f6e]" />
            </div>
          )}
        </div>
        <button data-testid="revenue-export-csv" onClick={exportCsv} disabled={rows.length === 0}
          className="inline-flex items-center gap-2 rounded-xl bg-[#0e9f6e] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#0c8a60] transition-colors disabled:opacity-50">
          <Download size={15} /> Export CSV
        </button>
      </div>

      {/* Recent Revenue table */}
      <div className="rounded-2xl border border-[#0f1712]/10 overflow-hidden bg-white">
        <div className="px-5 py-4 border-b border-[#0f1712]/8">
          <h3 className="font-display text-lg font-700 text-[#0f1712]">Recent Revenue</h3>
        </div>
        {rows.length === 0 ? (
          <div className="p-12 text-center text-[#0f1712]/45 text-sm">No transactions in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f6f6f2] text-[#0f1712]/50 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Date</th>
                  <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Merchant</th>
                  <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Customer</th>
                  <th className="text-right px-5 py-3 font-semibold whitespace-nowrap">Sale Amount</th>
                  <th className="text-right px-5 py-3 font-semibold whitespace-nowrap">Stripe Fee</th>
                  <th className="text-right px-5 py-3 font-semibold whitespace-nowrap">FLIX Revenue</th>
                  <th className="text-right px-5 py-3 font-semibold whitespace-nowrap">Merchant Net</th>
                  <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody data-testid="revenue-table-body">
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#0f1712]/8">
                    <td className="px-5 py-3.5 text-[#0f1712]/70 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-5 py-3.5 text-[#0f1712] font-medium whitespace-nowrap">{r.merchant}</td>
                    <td className="px-5 py-3.5 text-[#0f1712]/60 whitespace-nowrap">{r.customer}</td>
                    <td className="px-5 py-3.5 text-right text-[#0f1712]/80 whitespace-nowrap">{money(r.sale_amount)}</td>
                    <td className="px-5 py-3.5 text-right text-[#0f1712]/60 whitespace-nowrap">{money(r.stripe_fee)}</td>
                    <td className="px-5 py-3.5 text-right font-display font-700 text-[#0e9f6e] whitespace-nowrap">{money(r.flix_revenue)}</td>
                    <td className="px-5 py-3.5 text-right text-[#0f1712]/80 whitespace-nowrap">{money(r.merchant_net)}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenuePanel;
